import { Component, ElementRef, Inject, Optional, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { union } from 'lodash';
import { Subscription } from 'rxjs';

import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProjectsService } from '../../projects/projects.service';
import { stringToHexColor } from '../../shared/color.utils';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { SearchResult } from '../../shared/models/search/search-results.model';
import { TranslationBundle, TranslationService } from '../../translation.service';
import { SidebarService } from '../../sidebar.service';


interface DialogData {
  checkedContainers: Set<string>,
  resultType: string
}
@Component({
  host: {
    'class': 'mat-dialog-component',
    'test-id': 'modify-tags-dialog',
  },
  selector: 'modify-tags-dialog',
  styleUrls: ['./modify-tags-dialog.component.scss'],
  templateUrl: './modify-tags-dialog.component.html',
})
export class ModifyTagsDialog {
  @ViewChild('typeInputElementRemove', { static: true }) typeInputElementRemove: ElementRef<HTMLInputElement>
  @ViewChild('typeInputElementAdd', { static: true }) typeInputElementAdd: ElementRef<HTMLInputElement>
  @ViewChild('autoCompleteAdd', { static: false }) autoCompleteAdd: MatAutocomplete
  @ViewChild('autoCompleteRemove', { static: false }) autoCompleteRemove: MatAutocomplete
  @ViewChild('removeTagList') removeTagList;
  @ViewChild('addTagList') addTagList;

  form: FormGroup;
  availableTags: string[] = [];
  filteredTags: string[] = [];
  selectedTagsToRemove: string[] = [];
  selectedTagsToAdd: string[] = [];
  selectedContainers = Array.from(this.data.checkedContainers).map((containerId) => {
    return { id: containerId, type: this.data.resultType }; // format data for API request payload
  });

  private messages = TranslationBundle.EMPTY
  private subscriptions = {
    messages: Subscription.EMPTY,
    projectsService: Subscription.EMPTY,
    removeTagInput: Subscription.EMPTY,
    addTagInput: Subscription.EMPTY,
  }

  constructor(
    private coreApi: CoreApiService,
    private notifications: NotificationService,
    private dialog: MatDialogRef<ModifyTagsDialog>,
    private logger: LoggingService,
    private projectsService: ProjectsService,
    private translate: TranslationService,
    private sidebar: SidebarService,
    @Inject(MAT_DIALOG_DATA) @Optional() private data: DialogData,
  ) {
    dialog.disableClose = false;

    this.form = new FormGroup({
      removeTagInput: new FormControl('', [Validators.pattern(/^[^/]*$/), this.noDuplicates(this.selectedTagsToRemove)]),
      addTagInput: new FormControl('', [Validators.pattern(/^[^/]*$/), this.noDuplicates(this.selectedTagsToAdd)]),
    });
    this.subscriptions.messages = this.translate.getBundle('modifyTagsDialog').subscribe(messages => this.messages = messages);
    this.subscriptions.projectsService = this.projectsService.getGroups().subscribe((groups) => {
      this.availableTags = groups.reduce((allTags, group) => union(allTags, group.tags), []);
    });
    this.subscriptions.removeTagInput = this.form.get('removeTagInput').statusChanges.subscribe(
      status => this.removeTagList.errorState = status === 'INVALID'
    );
    this.subscriptions.addTagInput = this.form.get('addTagInput').statusChanges.subscribe(
      status => this.addTagList.errorState = status === 'INVALID'
    );
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  filterAutocomplete(): void {
    // this filters the autocomplete options for both add and remove sections
    const userInputRemove = this.form.controls.removeTagInput.value || '';
    const userInputAdd = this.form.controls.addTagInput.value || '';

    this.filteredTags = this.availableTags.filter(tag => {
      return (
        !this.selectedTagsToRemove.includes(tag) &&
        tag.toLowerCase().includes(userInputRemove.toLowerCase()) &&
        !this.selectedTagsToAdd.includes(tag) &&
        tag.toLowerCase().includes(userInputAdd.toLowerCase())
      );
    });
  }

  getTagColor(tag: string): string {
    return '#' + stringToHexColor(tag);
  }

  /**
   * Remove Tags Function Section
   */
  userCreatedTagToRemove(event: MatChipInputEvent): void {
    const { input } = event;

    if (this.autoCompleteRemove.isOpen && document.activeElement !== input) {
      return; // if we've selected a suggestion, don't capture the partial input
    }
    // Check to see if the tag is valid or not.
    if (this.form.controls.removeTagInput.invalid) {
      return;
    }

    if (this.form.value.removeTagInput) {
      this.selectedTagsToRemove.push(this.form.value.removeTagInput);
    }
    // reset the input
    this.typeInputElementRemove.nativeElement.value = '';
    this.form.controls.removeTagInput.reset();
  }

  autoCompleteRemoveTags(event: MatAutocompleteSelectedEvent): void {
    if (event.option.value) {
      this.selectedTagsToRemove.push(event.option.value);
    }
    // reset the input
    this.typeInputElementRemove.nativeElement.value = '';
    this.form.controls.removeTagInput.reset();
  }

  /**
   * Add Tags Function Section
   */
  userCreatedTagToAdd(event: MatChipInputEvent): void {
    const { input } = event;

    if (this.autoCompleteAdd.isOpen && document.activeElement !== input) {
      return; // if we've selected a suggestion, don't capture the partial input
    }
    // Check to see if the tag is valid or not.
    if (this.form.controls.addTagInput.invalid) {
      return;
    }

    if (this.form.value.addTagInput) {
      this.selectedTagsToAdd.push(this.form.value.addTagInput);
    }
    // reset the input
    this.typeInputElementAdd.nativeElement.value = '';
    this.form.controls.addTagInput.reset();
  }

  autoCompleteAddTags(event: MatAutocompleteSelectedEvent): void {
    if (event.option.value) {
      this.selectedTagsToAdd.push(event.option.value);
    }
    // reset the input
    this.typeInputElementAdd.nativeElement.value = '';
    this.form.controls.addTagInput.reset();
  }

  submit(): void {
    // Acquisition and Analyses have different IDs that need to be used
    let containers = this.selectedContainers;
    let searchResults: SearchResult[];
    const containerIds = Array.from(containers).map(container => container.id);

    if (this.data.resultType === ContainerType.Acquisition) {
      searchResults = this.sidebar.search.results$.value.filter(row => containerIds.includes(row._id));
      containers = searchResults.map((item) => {
        return { id: item._source.acquisition._id, type: this.data.resultType };
      });
    }
    if (this.data.resultType === ContainerType.Analysis) {
      searchResults = this.sidebar.search.results$.value.filter(row => containerIds.includes(row._id));
      containers = searchResults.map((item) => {
        return { id: item._source.analysis._id, type: this.data.resultType };
      });
    }

    if (this.selectedTagsToAdd.length > 0) {
      this.coreApi.bulk.tags.post({
        tags: this.selectedTagsToAdd,
        containers,
      }).subscribe(
        resp => {
          // On a 200, resp can contain both successful and failed tag submissions
          if (resp.success.length > 0) {
            this.notifications.success('modifyTagsDialog.addBulkTags.success');
          }
          if (resp.error.length > 0) {
            this.notifications.error('modifyTagsDialog.addBulkTags.error');
          }
          this.dialog.close(resp);
        },
        error => {
          this.logger.error(error);
          this.notifications.error('modifyTagsDialog.addBulkTags.error');
        }
      );
    }

    if (this.selectedTagsToRemove.length > 0) {
      this.coreApi.bulk.tags.delete({
        tags: this.selectedTagsToRemove,
        containers,
      }).subscribe(
        resp => {
          // On a 200, resp can contain both successful and failed tag submissions
          if (resp.success.length > 0) {
            this.notifications.success('modifyTagsDialog.removeBulkTags.success');
          }
          if (resp.error.length > 0) {
            this.notifications.error('modifyTagsDialog.removeBulkTags.error');
          }
          this.dialog.close(resp);
        },
        error => {
          this.logger.error(error);
          this.notifications.error('modifyTagsDialog.removeBulkTags.error');
        }
      );
    }
  }

  removeTagDelete(chip: string): void {
    const index = this.selectedTagsToRemove.indexOf(chip);

    if (index >= 0) {
      this.selectedTagsToRemove.splice(index, 1);
    }
  }

  addTagDelete(chip: string): void {
    const index = this.selectedTagsToAdd.indexOf(chip);

    if (index >= 0) {
      this.selectedTagsToAdd.splice(index, 1);
    }
  }

  private noDuplicates = (sourceList: string[]): ValidatorFn => (control: FormControl) => {
    if (sourceList.includes(control.value)) {
      return { noDuplicates: 'This tag already has already been added.' };
    }
    return null;
  }
}
